// Weather → Outfit recommendation rules engine

export const activityTypes = [
  { id: 'general', label: { fr: 'Général', en: 'General', nl: 'Algemeen' } },
  { id: 'running', label: { fr: 'Running', en: 'Running', nl: 'Hardlopen' } },
  { id: 'fitness', label: { fr: 'Fitness outdoor', en: 'Outdoor fitness', nl: 'Outdoor fitness' } },
  { id: 'walking', label: { fr: 'Marche', en: 'Walking', nl: 'Wandelen' } },
  { id: 'mobility', label: { fr: 'Mobilité / Stretching', en: 'Mobility / Stretching', nl: 'Mobiliteit / Stretching' } },
  { id: 'gentle', label: { fr: 'Séance douce', en: 'Gentle session', nl: 'Rustige sessie' } },
];

// Outfit items with SVG display config
export const outfitItems = {
  tshirt: { fr: 'T-shirt technique', en: 'Technical T-shirt', nl: 'Technisch T-shirt', zone: 'top' },
  tshirtBreathable: { fr: 'T-shirt respirant', en: 'Breathable T-shirt', nl: 'Ademend T-shirt', zone: 'top' },
  longSleeve: { fr: 'Haut manches longues', en: 'Long sleeve top', nl: 'Lange mouwen top', zone: 'top' },
  lightJacket: { fr: 'Veste légère', en: 'Light jacket', nl: 'Licht jasje', zone: 'layer' },
  windbreaker: { fr: 'Coupe-vent', en: 'Windbreaker', nl: 'Windjack', zone: 'layer' },
  warmJacket: { fr: 'Veste thermique', en: 'Thermal jacket', nl: 'Thermisch jasje', zone: 'layer' },
  rainJacket: { fr: 'Veste imperméable', en: 'Rain jacket', nl: 'Regenjack', zone: 'layer' },
  shorts: { fr: 'Short', en: 'Shorts', nl: 'Short', zone: 'bottom' },
  longPants: { fr: 'Pantalon long', en: 'Long pants', nl: 'Lange broek', zone: 'bottom' },
  leggings: { fr: 'Legging', en: 'Leggings', nl: 'Legging', zone: 'bottom' },
  sneakers: { fr: 'Chaussures de sport', en: 'Sport shoes', nl: 'Sportschoenen', zone: 'shoes' },
  lightShoes: { fr: 'Chaussures légères', en: 'Light shoes', nl: 'Lichte schoenen', zone: 'shoes' },
  gripShoes: { fr: 'Chaussures antidérapantes', en: 'Grip shoes', nl: 'Antislipschoenen', zone: 'shoes' },
};

export const accessories = {
  cap: { fr: 'Casquette', en: 'Cap', nl: 'Pet', icon: 'cap' },
  beanie: { fr: 'Bonnet', en: 'Beanie', nl: 'Muts', icon: 'beanie' },
  gloves: { fr: 'Gants', en: 'Gloves', nl: 'Handschoenen', icon: 'gloves' },
  sunglasses: { fr: 'Lunettes de soleil', en: 'Sunglasses', nl: 'Zonnebril', icon: 'sunglasses' },
  bottle: { fr: 'Gourde', en: 'Water bottle', nl: 'Waterfles', icon: 'bottle' },
  sunscreen: { fr: 'Crème solaire', en: 'Sunscreen', nl: 'Zonnebrandcrème', icon: 'sunscreen' },
  towel: { fr: 'Serviette d\'entraînement', en: 'Training towel', nl: 'Trainingshanddoek', icon: 'towel' },
  mat: { fr: 'Tapis de sol', en: 'Exercise mat', nl: 'Yogamat', icon: 'mat' },
  changeClothes: { fr: 'Vêtements de rechange', en: 'Change of clothes', nl: 'Reservekleding', icon: 'changeClothes' },
};

function getTemperatureCase(temp) {
  if (temp < 5) return 'cold';
  if (temp < 10) return 'cool';
  if (temp < 15) return 'mild';
  if (temp < 20) return 'temperate';
  if (temp < 25) return 'warm';
  return 'hot';
}

function isRainy(weather) {
  return weather.precip_mm > 0.5 || weather.condition_code >= 1063;
}

function isHeavyRain(weather) {
  return weather.precip_mm > 4 || (weather.condition_code >= 1192 && weather.condition_code <= 1282);
}

function isWindy(weather) {
  return weather.wind_kph > 30;
}

function isSunny(weather) {
  return weather.is_day === 1 && weather.cloud < 40 && weather.uv > 3;
}

export function getOutfitRecommendation(weather, activity = 'general', lang = 'fr') {
  const temp = weather.feels_like_c;
  const tempCase = getTemperatureCase(temp);
  const rainy = isRainy(weather);
  const heavyRain = isHeavyRain(weather);
  const windy = isWindy(weather);
  const sunny = isSunny(weather);

  let outfit = { top: [], layer: [], bottom: [], shoes: [], accessories: [] };
  let message = '';
  let safetyTip = '';
  let activityTip = '';
  let severity = 'good'; // good, caution, warning

  // --- Temperature-based base outfit ---
  switch (tempCase) {
    case 'cold':
      outfit.top.push('longSleeve');
      outfit.layer.push('warmJacket');
      outfit.bottom.push('longPants');
      outfit.shoes.push('sneakers');
      outfit.accessories.push('beanie', 'gloves');
      message = {
        fr: 'Temps froid : couvrez-vous bien avec des couches chaudes.',
        en: 'Cold weather: layer up with warm clothing.',
        nl: 'Koud weer: kleed u warm met meerdere lagen.',
      }[lang];
      break;
    case 'cool':
      outfit.top.push('tshirt');
      outfit.layer.push('windbreaker');
      outfit.bottom.push('longPants');
      outfit.shoes.push('sneakers');
      message = {
        fr: 'Temps frais : un t-shirt technique avec un coupe-vent sera parfait.',
        en: 'Cool weather: a technical t-shirt with a windbreaker is ideal.',
        nl: 'Fris weer: een technisch T-shirt met windjack is ideaal.',
      }[lang];
      break;
    case 'mild':
      outfit.top.push('tshirt');
      outfit.layer.push('lightJacket');
      outfit.bottom.push('shorts');
      outfit.shoes.push('sneakers');
      message = {
        fr: 'Température douce. Une tenue légère avec une veste fine est idéale.',
        en: 'Mild temperature. A light outfit with a thin jacket is ideal.',
        nl: 'Mild weer. Een lichte outfit met dun jasje is ideaal.',
      }[lang];
      break;
    case 'temperate':
      outfit.top.push('tshirt');
      outfit.bottom.push('shorts');
      outfit.shoes.push('sneakers');
      outfit.accessories.push('bottle');
      message = {
        fr: 'Temps agréable et sec : t-shirt et short, parfait pour bouger !',
        en: 'Pleasant dry weather: t-shirt and shorts, perfect for working out!',
        nl: 'Aangenaam droog weer: T-shirt en short, perfect om te bewegen!',
      }[lang];
      break;
    case 'warm':
      outfit.top.push('tshirtBreathable');
      outfit.bottom.push('shorts');
      outfit.shoes.push('lightShoes');
      outfit.accessories.push('cap', 'bottle');
      message = {
        fr: 'Temps chaud et ensoleillé. Hydratez-vous et protégez-vous du soleil.',
        en: 'Hot and sunny. Stay hydrated and protect yourself from the sun.',
        nl: 'Warm en zonnig. Blijf gehydrateerd en bescherm u tegen de zon.',
      }[lang];
      break;
    case 'hot':
      outfit.top.push('tshirtBreathable');
      outfit.bottom.push('shorts');
      outfit.shoes.push('lightShoes');
      outfit.accessories.push('cap', 'bottle', 'sunscreen');
      severity = 'caution';
      message = {
        fr: 'Très chaud : tenue ultra-légère, hydratation maximale, évitez les heures les plus chaudes.',
        en: 'Very hot: ultra-light outfit, maximum hydration, avoid peak hours.',
        nl: 'Zeer warm: ultralichte outfit, maximale hydratatie, vermijd piekuren.',
      }[lang];
      safetyTip = {
        fr: 'Privilégiez les séances tôt le matin ou en fin de journée.',
        en: 'Prefer early morning or evening sessions.',
        nl: 'Geef de voorkeur aan vroege ochtend- of avondsessies.',
      }[lang];
      break;
    default:
      break;
  }

  // --- Rain overlay ---
  if (heavyRain) {
    outfit.layer = ['rainJacket'];
    outfit.shoes = ['gripShoes'];
    outfit.accessories.push('changeClothes');
    severity = 'warning';
    message = {
      fr: 'Forte pluie prévue. Séance outdoor à adapter, option indoor recommandée.',
      en: 'Heavy rain expected. Adapt outdoor session, indoor option recommended.',
      nl: 'Zware regen verwacht. Pas outdoor sessie aan, indoor optie aanbevolen.',
    }[lang];
    activityTip = {
      fr: 'Prévoir adaptation indoor si météo instable.',
      en: 'Consider indoor adaptation if weather is unstable.',
      nl: 'Overweeg indoor aanpassing als het weer onstabiel is.',
    }[lang];
  } else if (rainy) {
    if (!outfit.layer.includes('rainJacket')) outfit.layer.push('rainJacket');
    outfit.shoes = ['gripShoes'];
    outfit.accessories.push('changeClothes');
    safetyTip = {
      fr: 'Pluie légère : prévoyez des vêtements de rechange.',
      en: 'Light rain: bring a change of clothes.',
      nl: 'Lichte regen: neem reservekleding mee.',
    }[lang];
  }

  // --- Wind overlay ---
  if (windy) {
    if (!outfit.layer.some(l => l === 'windbreaker' || l === 'warmJacket')) {
      outfit.layer.push('windbreaker');
    }
    if (!outfit.accessories.includes('gloves') && temp < 10) {
      outfit.accessories.push('gloves');
    }
  }

  // --- Sun overlay ---
  if (sunny && !heavyRain) {
    if (!outfit.accessories.includes('cap')) outfit.accessories.push('cap');
    if (!outfit.accessories.includes('sunglasses')) outfit.accessories.push('sunglasses');
    if (weather.uv >= 6 && !outfit.accessories.includes('sunscreen')) {
      outfit.accessories.push('sunscreen');
    }
  }

  // --- Activity adjustments ---
  if (activity === 'running') {
    if (tempCase !== 'cold') outfit.top = ['tshirtBreathable'];
    if (!outfit.accessories.includes('bottle')) outfit.accessories.push('bottle');
    if (!outfit.accessories.includes('towel')) outfit.accessories.push('towel');
  }
  if (activity === 'gentle' || activity === 'mobility') {
    if (tempCase === 'mild' || tempCase === 'temperate') {
      if (!outfit.layer.includes('lightJacket')) outfit.layer.push('lightJacket');
    }
    // Mat for stretching/yoga/gentle outdoor activities
    if (!outfit.accessories.includes('mat')) outfit.accessories.push('mat');
    if (!outfit.accessories.includes('towel')) outfit.accessories.push('towel');
  }
  if (activity === 'fitness') {
    // Training towel + exercise mat for outdoor strength training
    if (!outfit.accessories.includes('towel')) outfit.accessories.push('towel');
    if (!outfit.accessories.includes('mat')) outfit.accessories.push('mat');
  }
  if (activity === 'walking') {
    if (!outfit.accessories.includes('bottle')) outfit.accessories.push('bottle');
  }

  // Always add bottle for any activity
  if (!outfit.accessories.includes('bottle')) outfit.accessories.push('bottle');

  // --- Activity tip ---
  if (!activityTip) {
    if (severity === 'good') {
      activityTip = {
        fr: 'Parfait pour course légère, bootcamp ou marche active.',
        en: 'Perfect for light running, bootcamp or active walking.',
        nl: 'Perfect voor licht hardlopen, bootcamp of actief wandelen.',
      }[lang];
    } else if (severity === 'caution') {
      activityTip = {
        fr: 'Privilégiez les activités douces et restez à l\'ombre.',
        en: 'Prefer gentle activities and stay in the shade.',
        nl: 'Geef de voorkeur aan rustige activiteiten en blijf in de schaduw.',
      }[lang];
    }
  }

  // Dedupe accessories
  outfit.accessories = [...new Set(outfit.accessories)];

  return { outfit, message, safetyTip, activityTip, severity, tempCase };
}
